 
import { DataKey } from '../lib/model';
import {
	useEffect,
	useState,
	createContext,
	ReactNode,
	useContext
} from 'react';
import { useData } from './DataProvider';
import { useTheme } from '@mui/joy';
import { useRoute } from './RouteProvider';
import { STATES_WITH_EDGE_CASES } from '../lib/const';
//import { max } from 'd3-array';
//import { STATES_WITH_EDGE_CASES } from '../lib/const';
//import { useEdgeCases } from './EdgeCaseProvider';

export interface DonorSectionContextInterface {
	chartData: dataObjArray[][][];
}

type dataObjArray = {
	top: number;
	value: number;
	numCand: string;
	partyLabel: string;
	label: string;
	group: string;
	color: string;
	chamber: string;
	stat: string;
	chartCol: string;
	officeLevel: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export const DONOR_SECTION_CONTEXT_DEFAULT: DonorSectionContextInterface = {
	chartData: []
};

const DonorSectionContext = createContext(DONOR_SECTION_CONTEXT_DEFAULT);

// eslint-disable-next-line react-refresh/only-export-components
export function useDonorSectionData() {
	return useContext<DonorSectionContextInterface>(DonorSectionContext);
}
// interface Row {
// 	[x: string]: string;
// }
interface IProps {
	topic: string;
	children: ReactNode;
	page: string;
}
export default function DonorSectionProvider({
	topic,
	children,
	page
}: IProps) {
	const theme = useTheme();
	const {
		donorStateHouse10Money,
		donorStateSenate10Money,
		donorStateWide10Money,
		donorUsSenate10Money,
		donorUsHouse10Money,
		donorStateHouse10Contrib,
		donorStateSenate10Contrib,
		donorStateWide10Contrib,
		donorUsSenate10Contrib,
		donorUsHouse10Contrib,
		donorUsSenateMoney,
		donorUsHouseMoney,
		donorUsSenateCont,
		donorUsHouseCont
	} = useData();
	const { selectedState, election, donorTab, seat, raceFilter } = useRoute();
	const [chartData, setChartData] = useState<dataObjArray[][][]>(
		DONOR_SECTION_CONTEXT_DEFAULT.chartData
	);

	//for when vals aren't entered correctly
	const repPartyVals = [
		'Republican',
		'Republican Party',
		'Republican party',
		'REPUBLICAN PARTY',
		'REPUBLICAN'
	];
	const demPartyVals = [
		'Democratic',
		'Democratic Party',
		'Democratic party',
		'DEMOCRATIC PARTY',
		'DEMOCRATIC'
	];

	const hasEdgeCases =
		STATES_WITH_EDGE_CASES.includes(selectedState as string) &&
		page === 'donorState';

	useEffect(() => {
		const labels = [
			'Democratic Men',
			'Democratic Women',
			'Republican Women',
			'Republican Men'
		];
		const colors = [
			theme.colorSchemes.light.palette.categoryPartyDemocratMen.solidBg,
			theme.colorSchemes.light.palette.categoryPartyDemocratWomen.solidBg,
			theme.colorSchemes.light.palette.categoryPartyRepublicanWomen.solidBg,
			theme.colorSchemes.light.palette.categoryPartyRepublicanMen.solidBg
		];

		const compileData = (
			data: { [x: string]: string }[] | null,
			chartKey: string
		) => {
			const demMen = data?.filter(
				(d: { [x: string]: string }) =>
					demPartyVals.includes(d[DataKey.Party]) && d[DataKey.Gender] === 'Man'
			)[0];
			const demWomen = data?.filter(
				(d: { [x: string]: string }) =>
					demPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Woman'
			)[0];
			const repWomen = data?.filter(
				(d: { [x: string]: string }) =>
					repPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Woman'
			)[0];
			const repMen = data?.filter(
				(d: { [x: string]: string }) =>
					repPartyVals.includes(d[DataKey.Party]) && d[DataKey.Gender] === 'Man'
			)[0];

			const groupData = [demMen, demWomen, repWomen, repMen] as {
				[x: string]: string;
			}[];

			const getValue = (
				d: { [x: string]: string | number },
				chartKey: string
			) => {
				const val =
					chartKey === DataKey.DonorMen
						? 1 - +d[DataKey.DonorWomen]
						: chartKey === DataKey.DonorNonCollegeEd
						? 1 - +d[DataKey.DonorCollegeEd]
						: d[chartKey];
				return isNaN(val as number) ? 'na' : +val;
			};
			const dataObj = groupData
				.map((d, i) => {
					return d && d[chartKey] !== 'na'
						? {
								value: getValue(d, chartKey),
								numCand: d[DataKey.NumCandInGrp],
								partyLabel: i <= 1 ? 'DEM' : 'GOP',
								label: labels[i],
								color: colors[i],
								chamber: d[DataKey.Chamber], //these are just for checking to see if have correct data
								stat: d[DataKey.DonorStat],
								chartCol: chartKey,
								officeLevel: d[DataKey.DonorOfficeLevel]
						  }
						: null;
				})
				.filter((item) => item)
				.filter((item) => item && item.value !== 'na') as dataObjArray[];

			return dataObj;
		};

		const compileNationalData = (
			data: { [x: string]: string }[] | null,
			chartKey: string,
			filtered: boolean
		) => {
			const demMen = data?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.DonorNatDem] === '1' &&
					d[DataKey.DonorNatMan] === '1' &&
					d[seat[0]] === '1'
			)[0];
			const demWom = data?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.DonorNatDem] === '1' &&
					d[DataKey.DonorNatWom] === '1' &&
					d[DataKey.DonorNatWomAll] === '1' &&
					d[seat[0]] === '1'
			)[0];
			const repMen = data?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.DonorNatRep] === '1' &&
					d[DataKey.DonorNatMan] === '1' &&
					d[seat[0]] === '1'
			)[0];
			const repWom = data?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.DonorNatRep] === '1' &&
					d[DataKey.DonorNatWom] === '1' &&
					d[DataKey.DonorNatWomAll] === '1' &&
					d[seat[0]] === '1'
			)[0];

			const groupData = [demMen, demWom, repWom, repMen] as {
				[x: string]: string;
			}[];

			let womDemB, womDemW, womDemL, womDemAP, womDemMENA, womDemNatAm;

			let womRepB, womRepW, womRepL, womRepAP, womRepMENA, womRepNatAm;

			if (filtered) {
				womDemB = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatBlack] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womDemW = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatWhite] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womDemL = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatLat] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womDemAP = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatAsian] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womDemMENA = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatMena] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womDemNatAm = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatDem] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatNative] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepB = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatBlack] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepW = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatWhite] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepL = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatLat] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepAP = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatAsian] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepMENA = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatMena] === '1' &&
						d[seat[0]] === '1'
				)[0];
				womRepNatAm = data?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.DonorNatRep] === '1' &&
						d[DataKey.DonorNatWom] === '1' &&
						d[DataKey.DonorNatNative] === '1' &&
						d[seat[0]] === '1'
				)[0];
			}

			const filteredGroupData = [
				demMen,
				womDemAP,
				womDemB,
				womDemL,
				womDemMENA,
				womDemNatAm,
				womDemW,

				repMen,
				womRepAP,
				womRepB,
				womRepL,
				womRepMENA,
				womRepNatAm,
				womRepW
			];

			const getColor = (i: number) => {
				return i === 0
					? colors[0]
					: i < 7
					? colors[1]
					: i === 7
					? colors[2]
					: colors[3];
			};
			const groups = [
				'Men, All Races',
				'AAPI Women',
				'Black Women',
				'Latina Women',
				'MENA Women',
				'NatAm Women',
				'White Women',
				'Men, All Races',
				'AAPI Women',
				'Black Women',
				'Latina Women',
				'MENA Women',
				'NatAm Women',
				'White Women'
			];

			const getValue = (
				d: { [x: string]: string | number },
				chartKey: string
			) => {
				const val =
					chartKey === DataKey.DonorMen
						? 1 - +d[DataKey.DonorWomen]
						: chartKey === DataKey.DonorNonCollegeEd
						? 1 - +d[DataKey.DonorCollegeEd]
						: d[chartKey];
				return isNaN(val as number) && filtered
					? 0
					: isNaN(val as number)
					? 'na'
					: +val;
			};
			const group = filtered ? filteredGroupData : groupData;
			const dataObj = group
				.map((d, i) => {
					return d && d[chartKey] !== 'na'
						? {
								value: getValue(d, chartKey),
								numCand: d[DataKey.NumCandInGrp],
								partyLabel: i < 7 ? 'DEM' : 'GOP',
								label: filtered
									? i < 7
										? 'Democratic'
										: 'Republican'
									: labels[i],
								group: filtered ? groups[i] : '',
								color: filtered ? getColor(i) : colors[i],
								chamber: '', //these are just for checking to see if have correct data
								stat: d[DataKey.DonorStat],
								chartCol: chartKey,
								officeLevel: d[DataKey.DonorOffice]
						  }
						: null;
				})
				.filter((item) => item)
				.filter((item) => item && item.value !== 'na') as dataObjArray[];

			return dataObj;
		};

		const makeDataObjArray = () => {
			if (election === 'state' && page === 'donorState') {
				if (topic === 'gender') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorMen),
								compileData(donorStateHouse10Money, DataKey.DonorMen),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorMen)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorWomen),
								compileData(donorStateHouse10Money, DataKey.DonorWomen),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorWomen)
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorMen),
								compileData(donorStateHouse10Contrib, DataKey.DonorMen),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorMen)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorWomen),
								compileData(donorStateHouse10Contrib, DataKey.DonorWomen),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorWomen)
									: null
							].filter((item) => item)
						];
					}
				} else if (topic === 'education') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorCollegeEd),
								compileData(donorStateHouse10Money, DataKey.DonorCollegeEd),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorCollegeEd)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorNonCollegeEd),
								compileData(donorStateHouse10Money, DataKey.DonorNonCollegeEd),
								hasEdgeCases
									? compileData(
											donorStateWide10Money,
											DataKey.DonorNonCollegeEd
									  )
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorCollegeEd),
								compileData(donorStateHouse10Contrib, DataKey.DonorCollegeEd),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorCollegeEd)
									: null
							].filter((item) => item),
							[
								compileData(
									donorStateSenate10Contrib,
									DataKey.DonorNonCollegeEd
								),
								compileData(
									donorStateHouse10Contrib,
									DataKey.DonorNonCollegeEd
								),
								hasEdgeCases
									? compileData(
											donorStateWide10Contrib,
											DataKey.DonorNonCollegeEd
									  )
									: null
							].filter((item) => item)
						];
					}
				} else if (topic === 'race') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorAsian),
								compileData(donorStateHouse10Money, DataKey.DonorAsian),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorAsian)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorBlack),
								compileData(donorStateHouse10Money, DataKey.DonorBlack),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorBlack)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorLatino),
								compileData(donorStateHouse10Money, DataKey.DonorLatino),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorLatino)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorWhite),
								compileData(donorStateHouse10Money, DataKey.DonorWhite),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorWhite)
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorAsian),
								compileData(donorStateHouse10Contrib, DataKey.DonorAsian),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorAsian)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorBlack),
								compileData(donorStateHouse10Contrib, DataKey.DonorBlack),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorBlack)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorLatino),
								compileData(donorStateHouse10Contrib, DataKey.DonorLatino),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorLatino)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorWhite),
								compileData(donorStateHouse10Contrib, DataKey.DonorWhite),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorWhite)
									: null
							].filter((item) => item)
						];
					}
				} else if (topic === 'maritalStatus') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorMarried),
								compileData(donorStateHouse10Money, DataKey.DonorMarried),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorMarried)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorUnmarried),
								compileData(donorStateHouse10Money, DataKey.DonorUnmarried),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorUnmarried)
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorMarried),
								compileData(donorStateHouse10Contrib, DataKey.DonorMarried),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorMarried)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorUnmarried),
								compileData(donorStateHouse10Contrib, DataKey.DonorUnmarried),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorUnmarried)
									: null
							].filter((item) => item)
						];
					}
				} else if (topic === 'party') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorDem),
								compileData(donorStateHouse10Money, DataKey.DonorDem),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorDem)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorInd),
								compileData(donorStateHouse10Money, DataKey.DonorInd),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorInd)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.DonorRep),
								compileData(donorStateHouse10Money, DataKey.DonorRep),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorRep)
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorDem),
								compileData(donorStateHouse10Contrib, DataKey.DonorDem),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorDem)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorInd),
								compileData(donorStateHouse10Contrib, DataKey.DonorInd),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorInd)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorRep),
								compileData(donorStateHouse10Contrib, DataKey.DonorRep),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorRep)
									: null
							].filter((item) => item)
						];
					}
				} else if (topic === 'age') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorStateSenate10Money, DataKey.DonorUnder40),
								compileData(donorStateHouse10Money, DataKey.DonorUnder40),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.DonorUnder40)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.Donor40_64),
								compileData(donorStateHouse10Money, DataKey.Donor40_64),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.Donor40_64)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Money, DataKey.Donor65Plus),
								compileData(donorStateHouse10Money, DataKey.Donor65Plus),
								hasEdgeCases
									? compileData(donorStateWide10Money, DataKey.Donor65Plus)
									: null
							].filter((item) => item)
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorStateSenate10Contrib, DataKey.DonorUnder40),
								compileData(donorStateHouse10Contrib, DataKey.DonorUnder40),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.DonorUnder40)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.Donor40_64),
								compileData(donorStateHouse10Contrib, DataKey.Donor40_64),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.Donor40_64)
									: null
							].filter((item) => item),
							[
								compileData(donorStateSenate10Contrib, DataKey.Donor65Plus),
								compileData(donorStateHouse10Contrib, DataKey.Donor65Plus),
								hasEdgeCases
									? compileData(donorStateWide10Contrib, DataKey.Donor65Plus)
									: null
							].filter((item) => item)
						];
					}
				}
			} else if (election === 'congress' && page === 'donorState') {
				if (topic === 'gender') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorMen),
								compileData(donorUsHouse10Money, DataKey.DonorMen)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorWomen),
								compileData(donorUsHouse10Money, DataKey.DonorWomen)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorMen),
								compileData(donorUsHouse10Contrib, DataKey.DonorMen)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorWomen),
								compileData(donorUsHouse10Contrib, DataKey.DonorWomen)
							]
						];
					}
				} else if (topic === 'education') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorCollegeEd),
								compileData(donorUsHouse10Money, DataKey.DonorCollegeEd)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorNonCollegeEd),
								compileData(donorUsHouse10Money, DataKey.DonorNonCollegeEd)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorCollegeEd),
								compileData(donorUsHouse10Contrib, DataKey.DonorCollegeEd)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorNonCollegeEd),
								compileData(donorUsHouse10Contrib, DataKey.DonorNonCollegeEd)
							]
						];
					}
				} else if (topic === 'race') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorAsian),
								compileData(donorUsHouse10Money, DataKey.DonorAsian)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorBlack),
								compileData(donorUsHouse10Money, DataKey.DonorBlack)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorLatino),
								compileData(donorUsHouse10Money, DataKey.DonorLatino)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorWhite),
								compileData(donorUsHouse10Money, DataKey.DonorWhite)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorAsian),
								compileData(donorUsHouse10Contrib, DataKey.DonorAsian)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorBlack),
								compileData(donorUsHouse10Contrib, DataKey.DonorBlack)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorLatino),
								compileData(donorUsHouse10Contrib, DataKey.DonorLatino)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorWhite),
								compileData(donorUsHouse10Contrib, DataKey.DonorWhite)
							]
						];
					}
				} else if (topic === 'maritalStatus') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorMarried),
								compileData(donorUsHouse10Money, DataKey.DonorMarried)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorUnmarried),
								compileData(donorUsHouse10Money, DataKey.DonorUnmarried)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorMarried),
								compileData(donorUsHouse10Contrib, DataKey.DonorMarried)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorUnmarried),
								compileData(donorUsHouse10Contrib, DataKey.DonorUnmarried)
							]
						];
					}
				} else if (topic === 'party') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorDem),
								compileData(donorUsHouse10Money, DataKey.DonorDem)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorInd),
								compileData(donorUsHouse10Money, DataKey.DonorInd)
							],
							[
								compileData(donorUsSenate10Money, DataKey.DonorRep),
								compileData(donorUsHouse10Money, DataKey.DonorRep)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorDem),
								compileData(donorUsHouse10Contrib, DataKey.DonorDem)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorInd),
								compileData(donorUsHouse10Contrib, DataKey.DonorInd)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorRep),
								compileData(donorUsHouse10Contrib, DataKey.DonorRep)
							]
						];
					}
				} else if (topic === 'age') {
					if (donorTab === 0) {
						return [
							[
								compileData(donorUsSenate10Money, DataKey.DonorUnder40),
								compileData(donorUsHouse10Money, DataKey.DonorUnder40)
							],
							[
								compileData(donorUsSenate10Money, DataKey.Donor40_64),
								compileData(donorUsHouse10Money, DataKey.Donor40_64)
							],
							[
								compileData(donorUsSenate10Money, DataKey.Donor65Plus),
								compileData(donorUsHouse10Money, DataKey.Donor65Plus)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileData(donorUsSenate10Contrib, DataKey.DonorUnder40),
								compileData(donorUsHouse10Contrib, DataKey.DonorUnder40)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.Donor40_64),
								compileData(donorUsHouse10Contrib, DataKey.Donor40_64)
							],
							[
								compileData(donorUsSenate10Contrib, DataKey.Donor65Plus),
								compileData(donorUsHouse10Contrib, DataKey.Donor65Plus)
							]
						];
					}
				}
			} else if (page === 'donorNational') {
				const filtered = raceFilter[0] === 'true';
				if (topic === 'gender') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorMen,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorMen,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorWomen,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorWomen,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorMen,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorMen,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorWomen,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorWomen,
									filtered
								)
							]
						];
					}
				} else if (topic === 'education') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorCollegeEd,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorCollegeEd,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorNonCollegeEd,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorNonCollegeEd,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorCollegeEd,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorCollegeEd,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorNonCollegeEd,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorNonCollegeEd,
									filtered
								)
							]
						];
					}
				} else if (topic === 'race') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorAsian,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorAsian,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorBlack,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorBlack,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorLatino,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorLatino,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorWhite,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorWhite,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorAsian,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorAsian,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorBlack,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorBlack,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorLatino,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorLatino,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorWhite,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorWhite,
									filtered
								)
							]
						];
					}
				} else if (topic === 'maritalStatus') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorMarried,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorMarried,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorUnmarried,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorUnmarried,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorMarried,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorMarried,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorUnmarried,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorUnmarried,
									filtered
								)
							]
						];
					}
				} else if (topic === 'party') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorDem,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorDem,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorInd,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorInd,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorRep,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorRep,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorDem,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorDem,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorInd,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorInd,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorRep,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorRep,
									filtered
								)
							]
						];
					}
				} else if (topic === 'age') {
					if (donorTab === 0) {
						return [
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.DonorUnder40,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.DonorUnder40,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.Donor40_64,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.Donor40_64,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateMoney,
									DataKey.Donor65Plus,
									filtered
								),
								compileNationalData(
									donorUsHouseMoney,
									DataKey.Donor65Plus,
									filtered
								)
							]
						];
					} else if (donorTab === 1) {
						return [
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.DonorUnder40,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.DonorUnder40,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.Donor40_64,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.Donor40_64,
									filtered
								)
							],
							[
								compileNationalData(
									donorUsSenateCont,
									DataKey.Donor65Plus,
									filtered
								),
								compileNationalData(
									donorUsHouseCont,
									DataKey.Donor65Plus,
									filtered
								)
							]
						];
					}
				}
			}
		};

		const dataObjArray = makeDataObjArray() as dataObjArray[][][];
		setChartData(dataObjArray);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		donorStateHouse10Money,
		donorStateSenate10Money,
		donorStateWide10Money,
		donorUsSenate10Money,
		donorUsHouse10Money,
		donorStateHouse10Contrib,
		donorStateSenate10Contrib,
		donorStateWide10Contrib,
		donorUsSenate10Contrib,
		donorUsHouse10Contrib,
		donorUsSenateMoney,
		donorUsHouseMoney,
		donorUsSenateCont,
		donorUsHouseCont,
		election,
		donorTab,
		page,
		seat,
		raceFilter
	]);

	return (
		<DonorSectionContext.Provider
			value={{
				chartData
			}}
		>
			{children}
		</DonorSectionContext.Provider>
	);
}
